export const ALL = 'all'

export interface ProductFilter<TOption extends string> {
  name: string
  legend: string
  enabled: Set<TOption>
  options: ReadonlyArray<{ title: string, tagName: string, value: TOption }>
}

export function useProductFilters(products: MaybeRefOrGetter<Array<Packet | SingleProduct> | undefined>) {
  const route = useRoute()

  const all = computed(() => toValue(products)?.map(toProductCard) ?? [])

  const levelOptions = computed(() => new Set(all.value.flatMap((it) => it.levels)))

  const filteredByLevel = computed(() => all.value.filter(byLevels(route.query)))

  const filtered = computed(
    () => filteredByLevel.value
      .filter(bySubjects(route.query))
      .filter(byFormats(route.query)),
  )

  const filteredGroups = computed<ProductCard<Packet | SingleProduct>[][]>(
    () => {
      // Group all cards by their type, since we want to sort packets above single products
      const grouped = filtered.value.reduce((acc, curr) => {
        if (curr.type === 'packet') {
          acc.packets.push(curr as ProductCard<Packet>)
        } else if (curr.type === 'single-product') {
          acc.singleProducts.push(curr as ProductCard<SingleProduct>)
        }
        return acc
      }, {
        packets: [] as ProductCard<Packet>[],
        singleProducts: [] as ProductCard<SingleProduct>[],
      })

      // If we only have 1 or fewer packets, the single products should fill up the rest of the row, so we put them in the same group, which should then be rendered in a single list/grid
      if (grouped.packets.length <= 1) {
        return [
          [
            ...grouped.packets,
            ...grouped.singleProducts,
          ],
        ]
      }

      // If there are more packets (say 2, 3 or even 4), they should always have their own row, so we return two separate groups that will render two different lists/grids
      return [
        grouped.packets,
        grouped.singleProducts,
      ]
    },
  )

  return {
    filtered,
    filteredGroups,
    levels: [
      {
        value: ALL,
        label: 'Alle',
      },
      ...LEVEL_OPTIONS.filter((it) => levelOptions.value.has(it.value)).map((it) => ({
        value: it.value,
        label: it.title,
      })),
    ],
    filters: computed(() => [
      {
        name: 'subject',
        legend: 'FAG',
        enabled: new Set(filteredByLevel.value.flatMap((it) => it.subjectCategories)),
        options: SUBJECT_OPTIONS.filter(
          (it) => it.value !== 'norwegian-foreigners',
        ),
      } satisfies ProductFilter<SubjectCategory>,
      {
        name: 'format',
        legend: 'PRODUKTTYPE',
        enabled: new Set(filteredByLevel.value.flatMap((it) => it.formats)),
        options: PRODUCT_FORMAT_OPTIONS,
      } satisfies ProductFilter<ProductFormat>,
    ]),
  }
}
